import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  OrderWorkflow,
  PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody,
  PostAdminV1StoresStoreIdOrdersIdChangeRequestsBody,
  PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody,
} from '@nucleus-commerce/api-client/admin';

import { orderKeys } from '@/features/orders';

import { productionApi, type StageRef } from '../api/production-api';

import { productionKeys } from './production-keys';

type QueryClient = ReturnType<typeof useQueryClient>;

/**
 * A stage action changes the order too (labels, current stage, fulfilment, timeline), so the
 * order detail, the order lists and the board are refetched next to storing the workflow.
 */
function settle(
  queryClient: QueryClient,
  storeId: string,
  orderId: string,
  workflow?: OrderWorkflow,
) {
  if (workflow) queryClient.setQueryData(productionKeys.workflow(storeId, orderId), workflow);
  return Promise.all([
    workflow
      ? undefined
      : queryClient.invalidateQueries({ queryKey: productionKeys.workflow(storeId, orderId) }),
    queryClient.invalidateQueries({ queryKey: orderKeys.detail(storeId, orderId) }),
    queryClient.invalidateQueries({ queryKey: orderKeys.lists(storeId) }),
    queryClient.invalidateQueries({ queryKey: productionKeys.boards(storeId) }),
  ]);
}

function useStageAction<TVariables>(
  storeId: string,
  orderId: string,
  run: (ref: StageRef, variables: TVariables) => Promise<OrderWorkflow>,
  successMessage: string,
  options: { silentError?: boolean } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...variables }: { key: string } & TVariables) =>
      run({ storeId, orderId, key }, variables as TVariables),
    meta: { successMessage, silentError: options.silentError ?? true },
    onSuccess: (workflow) => settle(queryClient, storeId, orderId, workflow),
  });
}

/** Start and switch-on have no form, so their refusals are toasted globally. */
export function useStartStage(storeId: string, orderId: string) {
  return useStageAction<object>(
    storeId,
    orderId,
    (ref) => productionApi.start(ref),
    'production:toasts.started',
    {
      silentError: false,
    },
  );
}

export function useEnableStage(storeId: string, orderId: string) {
  return useStageAction<object>(
    storeId,
    orderId,
    (ref) => productionApi.enable(ref),
    'production:toasts.enabled',
    {
      silentError: false,
    },
  );
}

/** 422 DELAY_REASON_REQUIRED on `delayReason` when the stage ran late; the dialog asks for it. */
export function useCompleteStage(storeId: string, orderId: string) {
  return useStageAction<{ body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody }>(
    storeId,
    orderId,
    (ref, { body }) => productionApi.complete(ref, body),
    'production:toasts.completed',
  );
}

export function useSkipStage(storeId: string, orderId: string) {
  return useStageAction<{ body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody }>(
    storeId,
    orderId,
    (ref, { body }) => productionApi.skip(ref, body),
    'production:toasts.skipped',
  );
}

export function useUpdateStage(storeId: string, orderId: string, successMessage: string) {
  return useStageAction<{ body: PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody }>(
    storeId,
    orderId,
    (ref, { body }) => productionApi.update(ref, body),
    successMessage,
  );
}

export function useSubmitQc(storeId: string, orderId: string) {
  return useStageAction<{ body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody }>(
    storeId,
    orderId,
    (ref, { body }) => productionApi.submitQc(ref, body),
    'production:toasts.qcSubmitted',
  );
}

export function useSendStage(storeId: string, orderId: string, successMessage: string) {
  return useStageAction<{ body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody }>(
    storeId,
    orderId,
    (ref, { body }) => productionApi.send(ref, body),
    successMessage,
  );
}

export function useRecordParcels(storeId: string, orderId: string) {
  return useStageAction<{ body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody }>(
    storeId,
    orderId,
    (ref, { body }) => productionApi.parcels(ref, body),
    'production:toasts.parcelsSaved',
  );
}

export function useSwitchWorkflow(storeId: string, orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => productionApi.switchTemplate(storeId, orderId, templateId),
    meta: { successMessage: 'production:toasts.switched', silentError: true },
    onSuccess: (workflow) => settle(queryClient, storeId, orderId, workflow),
  });
}

export function useCreateChangeRequest(storeId: string, orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdOrdersIdChangeRequestsBody) =>
      productionApi.createChangeRequest(storeId, orderId, body),
    meta: { successMessage: 'production:toasts.changeAdded', silentError: true },
    onSuccess: () => settle(queryClient, storeId, orderId),
  });
}

export function useResolveChangeRequest(storeId: string, orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      body: PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody;
    }) => productionApi.resolveChangeRequest(storeId, orderId, requestId, body),
    meta: { successMessage: 'production:toasts.changeResolved', silentError: true },
    onSuccess: () => settle(queryClient, storeId, orderId),
  });
}
